import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Alert, Tooltip } from '@mui/material';
import PageContainer from 'components/PageContainer';
import { getQuestionList } from '@redux/actions/Investor';
import InvestorDetails from './InvestorDetails';
import InvestorCommunication from './InvestorCommunication';
import TaxAndBankDetails from './TaxAndBankDetails';
import InvestorAppropriateness from './InvestorAppropriateness';
import InvestorCertification from './InvestorCertification';
import KycResults from './KycResults';
import CoolOffStatus from './CoolOffStatus';
import InvestmentsTable from './InvestmentsTable';
import InvestorUpdates from './InvestorUpdates';
import InvestorAccessGrants from './InvestorAccessGrants';
import CashBalanceTable from './CashBalanceTable';
import InvestorChangeLog from './InvestorChangeLog';
import { downloadAppropriatenessPdf } from 'helpers/investor/downloadAppropriatenessPdf';
import Notes from 'components/Notes';
import { NoteRelation } from 'further-types/notes';
import Chip from 'components/Chip';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import PageSection from 'components/PageSection';
import Spacing from 'components/Spacing';
import api from 'lib/trpcClient';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const breadcrumbs = [
  { label: 'Dashboard' },
  { label: 'Investor', link: '/investor' },
  { label: 'View Investor', link: '/', isActive: true },
];

const InvestorDetail: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [canViewKyc, canViewUsers] = useHasPermissions([
    RolePermission.ReadInvestorKyc,
    RolePermission.ReadInvestorAccessGrant,
  ]);

  const [question, setQuestion] = useState([]);

  const { data: investorDetail, isLoading } = api.investor.getInvestor.useQuery(
    { investorId: id },
  );

  useEffect(() => {
    const getQuestion = async () => {
      const resp = await dispatch<any>(getQuestionList());
      if (resp.status === 200) {
        setQuestion(resp.data.data);
      }
    };
    getQuestion();
  }, [dispatch]);

  if (!investorDetail) {
    if (!isLoading) {
      return <Alert severity="info">Investor not found</Alert>;
    }
    return (
      <PageContainer heading="Investor: View Investor" loading></PageContainer>
    );
  }

  return (
    <PageContainer
      heading="Investor: View Investor"
      breadcrumbs={breadcrumbs}
      subheaderComponent={
        investorDetail.isAccountTemporary && (
          <Tooltip title="This is a temporary account for a new investor that has been invited to make an advised subscription. When the investor completes their subscription, this account will become a live investor account.">
            <div>
              <Chip variant="success">Pending advised investor</Chip>
            </div>
          </Tooltip>
        )
      }
    >
      <PageContent>
        <RecordView size="full">
          <InvestorDetails investorDetail={investorDetail} id={id} />
          <PageSection title="Investor communications" showDivider>
            <InvestorCommunication investorDetail={investorDetail} />
          </PageSection>
          <PageSection title="Tax and bank details" showDivider>
            <TaxAndBankDetails
              bankDetails={investorDetail.bankDetails}
              investorDetails={investorDetail}
            />
          </PageSection>

          <PageSection
            title="Investor appropriateness and suitability"
            showDivider
          >
            <Spacing size="sm">
              <InvestorCertification investorDetail={investorDetail} />
              <InvestorAppropriateness
                downloadQuestion={() =>
                  downloadAppropriatenessPdf({
                    investorDetail,
                    questions: question,
                  })
                }
                id={id}
                investorDetail={investorDetail}
              />
              <CoolOffStatus investorId={id} firmId={investorDetail.firmId} />
            </Spacing>
          </PageSection>
          {canViewKyc && (
            <PageSection showDivider>
              <KycResults
                investorId={id}
                fullName={investorDetail.fullName}
                identityVerificationCertificates={
                  investorDetail.identityVerificationCertificate
                }
                accountType={investorDetail.accountType}
              />
            </PageSection>
          )}

          <PageSection showDivider>
            <InvestmentsTable investorId={id} />
          </PageSection>
          <PageSection showDivider>
            <CashBalanceTable
              cashBalance={investorDetail.cashBalance}
              investorDetails={investorDetail}
              loading={isLoading}
            />
          </PageSection>

          <PageSection title="Investor updates" showDivider>
            <InvestorUpdates investorId={id} investorDetail={investorDetail} />
          </PageSection>

          {canViewUsers && (
            <PageSection showDivider>
              <InvestorAccessGrants
                investorId={id}
                firmId={investorDetail.firmId}
              />
            </PageSection>
          )}

          <PageSection title="Change log" showDivider>
            <InvestorChangeLog investorId={id} />
          </PageSection>
          <PageSection showDivider>
            <Notes relationId={id} noteRelation={NoteRelation.Investor} />
          </PageSection>
        </RecordView>
      </PageContent>
    </PageContainer>
  );
};
export default InvestorDetail;
